import {skipToken} from '@reduxjs/toolkit/query'
import * as React from 'react'

import {useAppSelector} from '../../../hooks/react-redux'
import {
  getBranchesLocator,
  getBuildTypeBranchesArg,
  getIsBranchPresentInBuildTypeArg,
  getIsBranchPresentInProjectArg,
  getProjectBranchesArg,
  Policy,
} from '../../../rest/branches'
import {getBuildType, getHasNonDefaultBranches, getProject} from '../../../selectors'
import {restApi} from '../../../services/rest'
import {isArray} from '../../../utils/guards'
import {subscribeOnBuildTypeEvents, subscribeOnProjectEvents} from '../../../utils/subscriber'
import {BUILD_TYPE_BRANCHES_CHANGED} from '../../../utils/subscriptionEvents'
import {useSequenceLoaderReadyHandler} from '../SequenceLoader/SequenceLoader.hooks'

import BranchMultipleSelect from './BranchMultipleSelect'
import BranchSelect from './BranchSelect'
import type {
  AddProps,
  BaseProps,
  BranchMultipleSelectBaseProps,
  BranchSelectBaseProps,
  OwnProps,
} from './BranchSelect.types'
import {matches} from './BranchSelect.utils'

function useWrapper<P extends BaseProps>({
  projectOrBuildTypeNode,
  policy,
  includeSnapshots,
  includeSubprojects,
  excludeGroups,
  selected,
  onLoad,
  ...restProps
}: OwnProps<P>): AddProps {
  const fetchBranchesParams = {
    node: projectOrBuildTypeNode,
    policy,
    includeSnapshots,
    includeSubprojects,
    excludeGroups,
  }
  const locator = getBranchesLocator(fetchBranchesParams)
  const selectedLocator =
    selected != null &&
    !isArray(selected) &&
    selected.wildcard !== true &&
    policy === Policy.ACTIVE_HISTORY_AND_ACTIVE_VCS_BRANCHES
      ? getBranchesLocator({
          node: projectOrBuildTypeNode,
          policy: Policy.ALL_BRANCHES,
          includeSnapshots: true,
          includeSubprojects: true,
          branch: selected,
          inPath: true,
        })
      : null
  const selectedItems = selected != null ? (Array.isArray(selected) ? selected : [selected]) : []

  const projectId =
    projectOrBuildTypeNode?.nodeType === 'project' ? projectOrBuildTypeNode.id : null
  const buildTypeId = projectOrBuildTypeNode?.nodeType === 'bt' ? projectOrBuildTypeNode.id : null

  const {branchesForProject, refetch: refetchForProject} =
    restApi.endpoints.getAllBranches.useQuery(
      projectId != null ? getProjectBranchesArg(projectId, locator) : skipToken,
      {selectFromResult: ({data}) => ({branchesForProject: data?.branch})},
    )
  const {branchesForBuildType, refetch: refetchForBuildType} =
    restApi.endpoints.getAllBranchesOfBuildType.useQuery(
      buildTypeId ? getBuildTypeBranchesArg(buildTypeId, locator) : skipToken,
      {selectFromResult: ({data}) => ({branchesForBuildType: data?.branch})},
    )
  const branches = branchesForProject ?? branchesForBuildType
  const hasNonDefaultBranches = getHasNonDefaultBranches(branches)

  const {selectedIsKnownInProject} = restApi.endpoints.checkIfBranchExists.useQuery(
    projectId != null && selectedLocator != null
      ? getIsBranchPresentInProjectArg(projectId, selectedLocator)
      : skipToken,
    {selectFromResult: ({data}) => ({selectedIsKnownInProject: data})},
  )
  const {selectedIsKnownInBuildType} = restApi.endpoints.checkIfBranchExistsInBuildType.useQuery(
    buildTypeId != null && selectedLocator != null
      ? getIsBranchPresentInBuildTypeArg(buildTypeId, selectedLocator)
      : skipToken,
    {selectFromResult: ({data}) => ({selectedIsKnownInBuildType: data})},
  )
  const selectedIsKnown = selectedIsKnownInProject ?? selectedIsKnownInBuildType

  const ready =
    branches != null &&
    (selectedItems.length === 0 ||
      branches.some(branch => selectedItems.some(item => matches(branch, item))) ||
      selectedLocator == null ||
      selectedIsKnown != null)
  const shouldBeVisible =
    hasNonDefaultBranches || (selected != null && !isArray(selected) && selected.default !== true)
  const defaultBranchVcsName = branches?.find(branch => branch.default)?.name
  const buildTypeInternalId = useAppSelector(
    state =>
      restProps.buildTypeInternalId ??
      (projectOrBuildTypeNode?.nodeType === 'bt'
        ? getBuildType(state, projectOrBuildTypeNode.id)?.internalId
        : null),
  )
  const projectInternalId = useAppSelector(
    state =>
      restProps.projectInternalId ??
      (projectOrBuildTypeNode?.nodeType === 'project'
        ? getProject(state, projectOrBuildTypeNode.id)?.internalId
        : null),
  )
  React.useEffect(() => {
    if (buildTypeInternalId != null) {
      return subscribeOnBuildTypeEvents(
        buildTypeInternalId,
        [BUILD_TYPE_BRANCHES_CHANGED],
        refetchForBuildType,
      )
    }

    if (projectInternalId != null) {
      return subscribeOnProjectEvents(
        projectInternalId,
        [BUILD_TYPE_BRANCHES_CHANGED],
        refetchForProject,
      )
    }

    return undefined
  }, [buildTypeInternalId, projectInternalId, refetchForBuildType, refetchForProject])
  React.useEffect(() => {
    if (onLoad != null && ready) {
      onLoad({
        shouldBeVisible,
        defaultBranchVcsName,
      })
    }
  }, [defaultBranchVcsName, onLoad, ready, shouldBeVisible])
  useSequenceLoaderReadyHandler(ready)
  return {
    locator,
    ready,
    selectedIsKnown,
    branches,
    hasNonDefaultBranches,
    shouldBeVisible,
    defaultBranchVcsName,
  }
}

export default function BranchSelectContainer(
  props: OwnProps<BranchSelectBaseProps>,
): React.ReactElement | null {
  const addProps = useWrapper(props)
  return addProps.shouldBeVisible ? (
    <BranchSelect {...props} {...addProps} key={addProps.locator} />
  ) : null
}
export function BranchMultipleSelectContainer(
  props: OwnProps<BranchMultipleSelectBaseProps>,
): React.ReactElement | null {
  const addProps = useWrapper(props)
  return addProps.shouldBeVisible ? (
    <BranchMultipleSelect {...props} {...addProps} key={addProps.locator} />
  ) : null
}
